/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Who runs this site, in one place.
 *
 * A comparison platform that will not say which legal entity publishes it
 * reads as an affiliate front. The identity therefore belongs in the footer
 * and in the contact colophon — but only once it is TRUE. The values below are
 * transcribed from the KvK handelsregister entry for KvK 42156364 — not from a
 * draft, an invoice or a founder's memory.
 *
 * The register's own `inschrijvingsdatum` is **2026-09-01**. The plan this
 * module was built against said 4 September, and a dozen comments across the
 * codebase repeated that planning date until the register was actually read;
 * the register wins, and the date now lives here and nowhere else, because a
 * fact copied into twelve places is how facts drift apart. Nothing renders it.
 *
 * The gate stays, because it still has work to do: anything not yet
 * established (today: the btw-identificatienummer) is an empty string, and an
 * empty string renders nothing at all — never a dash, never a placeholder,
 * never a half-filled row. See `hasPublishableIdentity`.
 *
 * Deliberately a plain module, not i18n: a KvK number and a legal name are
 * facts, identical in every language. Only the LABELS around them are
 * translated (`footer.identity_*`, `contact.colophon_*`).
 */

export interface OrgIdentity {
  /** Registered legal name, exactly as in the KvK handelsregister. */
  legalName: string;
  /** KvK number — 8 digits, no spaces. */
  kvk: string;
  /** Registered office (vestigingsplaats), city only. */
  city: string;
  /** Public contact mailbox. */
  email: string;
  /** Btw-identificatienummer (NL + 9 digits + B + 2). Optional. */
  vatId: string;
}

/**
 * Transcribed from the KvK handelsregister on 2026-09-05.
 *
 * An empty string below is "not established yet", never "unknown" — do not
 * guess, do not copy a number from a draft.
 *
 * `legalName` is the register's name VERBATIM: "ERE-Vergelijk.nl", exactly as
 * `huidigeNamen` and `huidigeHandelsNamen` carry it (a VOF has no statutaire
 * naam, so there is no fuller form to prefer). Appending " VOF" is tempting —
 * the bare string reads like this site's domain rather than like an entity —
 * but this value is also emitted as schema.org `legalName`, which is defined
 * as the registered company name, and a machine reading that field is entitled
 * to find the handelsregister agreeing with it word for word. If the rechtsvorm
 * should be visible to a human, it belongs in a colophon row of its own.
 *
 * `city` is the vestigingsplaats only. The hoofdvestiging's street address is
 * public in the handelsregister, but it is a home address and no surface here
 * needs it.
 *
 * `vatId` is issued by the Belastingdienst, separately from the KvK
 * registration, and is not in the public handelsregister — it stays empty
 * (and the colophon row stays absent) until the btw-id arrives.
 */
export const ORG_IDENTITY: OrgIdentity = {
  legalName: 'ERE-Vergelijk.nl',
  kvk: '42156364',
  city: 'Valkenswaard',
  email: 'info@ere-vergelijk.nl',
  vatId: '',
};

const filled = (value: string | undefined): boolean => Boolean(value && value.trim());

/**
 * Is there enough to publish an identity block at all?
 *
 * The bar is legal name + KvK number together: those two are what makes the
 * block checkable in the handelsregister. A name without a number is a claim,
 * not a credential, and a number without a name is unreadable — so neither
 * half is shown on its own. City and btw-id are additive; they render when
 * present and are simply left out when not.
 */
export const hasPublishableIdentity = (identity: OrgIdentity = ORG_IDENTITY): boolean =>
  filled(identity.legalName) && filled(identity.kvk);

/** Is there a contact mailbox to publish? Independent of the legal identity. */
export const hasPublishableContactEmail = (identity: OrgIdentity = ORG_IDENTITY): boolean =>
  filled(identity.email);

/**
 * The compact one-line form for the footer bottom bar: legal name, KvK number
 * and city, in that order, with every empty part dropped. Returns an empty
 * array when the identity is not publishable, so a caller that forgets to
 * check `hasPublishableIdentity` still renders nothing rather than a stray
 * fragment.
 *
 * The KvK number is returned unlabelled — the caller prefixes the translated
 * `footer.identity_kvk_label` so the label is in the visitor's language.
 */
export const identityFooterParts = (
  identity: OrgIdentity = ORG_IDENTITY
): { legalName: string; kvk: string; city: string } | null => {
  if (!hasPublishableIdentity(identity)) return null;
  return {
    legalName: identity.legalName.trim(),
    kvk: identity.kvk.trim(),
    city: filled(identity.city) ? identity.city.trim() : '',
  };
};

/**
 * Label/value rows for the contact colophon, in reading order, with every
 * empty value dropped. `id` is stable and maps to a `contact.colophon_*_label`
 * key; the caller does the translating (this module stays i18n-free, the
 * partnerCompleteness.ts pattern).
 */
export type ColophonRowId = 'legal_name' | 'kvk' | 'vat' | 'city' | 'email';

export const colophonRows = (
  identity: OrgIdentity = ORG_IDENTITY
): { id: ColophonRowId; value: string }[] => {
  const rows: { id: ColophonRowId; value: string }[] = [];
  if (hasPublishableIdentity(identity)) {
    rows.push({ id: 'legal_name', value: identity.legalName.trim() });
    rows.push({ id: 'kvk', value: identity.kvk.trim() });
    if (filled(identity.vatId)) rows.push({ id: 'vat', value: identity.vatId.trim() });
    if (filled(identity.city)) rows.push({ id: 'city', value: identity.city.trim() });
  }
  if (filled(identity.email)) rows.push({ id: 'email', value: identity.email.trim() });
  return rows;
};

/**
 * Anchor of the coverage explanation on /providers — why the NEa register
 * holds more parties than the comparison does. Exported here (rather than
 * hardcoded twice) because the explanation lives on one page and the claim it
 * explains lives on another: the hero's "N aanbieders" chip links straight to
 * it.
 */
export const COVERAGE_ANCHOR = 'dekking';

/**
 * Anchor of "Hoe verdienen wij dan geld?" on /hoe-wij-vergelijken — the
 * revenue model. Same reason as COVERAGE_ANCHOR: the comparer's trust strip
 * makes the claim (`comparison.trust_fee`) and links to the section that
 * explains it.
 */
export const REVENUE_MODEL_ANCHOR = 'verdienmodel';
